
function extractCommerceNiche(input = {}) {
    const text = [
        input.description,
        input.coreServices,
        input.projectTypes,
        input.subIndustry,
        input.businessName
    ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

    if (!text) return "";

    const nicheMap = [
        {
            keys: ["brass", "copper", "metal decor", "pooja", "diya", "idol", "mandir"],
            niche: "brass products",
            categories: ["Brass Decor", "Pooja Essentials", "Brass Gifts", "Traditional Homeware"],
            imageStrategy: "Warm premium product photography with brass texture, handcrafted details, traditional decor styling, clean ecommerce backgrounds",
            visualDirection: "Premium artisan commerce with warm gold tones and detailed product cards",
            sampleItems: [
                {
                    name: "Handcrafted Brass Diya Set",
                    subtitle: "Traditional festive lighting set",
                    description: "Elegant brass diya set crafted for pooja rooms, festive gifting, and home decor.",
                    badge: "Best Seller",
                    category: "Pooja Essentials",
                    audience: "Home decor and festive buyers",
                    level: "Premium",
                    imagePreference: "Warm studio product photo of brass diya set",
                    keyPoints: ["Pure brass finish", "Festive gifting", "Traditional design"]
                },
                {
                    name: "Antique Brass Ganesha Idol",
                    subtitle: "Premium spiritual decor piece",
                    description: "A detailed brass idol designed for pooja spaces, gifting, and elegant interiors.",
                    badge: "Premium",
                    category: "Brass Idols",
                    audience: "Spiritual and decor buyers",
                    level: "Premium",
                    imagePreference: "Close-up brass idol product photography",
                    keyPoints: ["Antique finish", "Gift-ready", "Detailed craftsmanship"]
                },
                {
                    name: "Brass Urli Bowl",
                    subtitle: "Decorative centerpiece for homes",
                    description: "A timeless brass urli bowl for flowers, floating candles, and festive decoration.",
                    badge: "Decor Pick",
                    category: "Brass Decor",
                    audience: "Home decor buyers",
                    level: "Luxury",
                    imagePreference: "Brass urli bowl with flowers in elegant home setting",
                    keyPoints: ["Home decor", "Festive styling", "Premium finish"]
                }
            ]
        },
        {
            keys: ["fashion", "clothes", "clothing", "apparel", "shirt", "kurti", "dress"],
            niche: "fashion apparel",
            categories: ["New Arrivals", "Ethnic Wear", "Casual Wear", "Accessories"],
            imageStrategy: "Fashion ecommerce photography with models, clean product cards, lifestyle collection shots",
            visualDirection: "Modern fashion storefront with editorial collection sections",
            sampleItems: []
        },
        {
            keys: ["jewellery", "jewelry", "ring", "necklace", "bracelet"],
            niche: "jewellery",
            categories: ["Rings", "Necklaces", "Bracelets", "Gift Sets"],
            imageStrategy: "Premium jewellery macro product photography with luxury lighting",
            visualDirection: "Luxury jewellery storefront with refined product detail sections",
            sampleItems: []
        },
        {
            keys: ["furniture", "chair", "sofa", "table", "wooden"],
            niche: "furniture",
            categories: ["Living Room", "Bedroom", "Office Furniture", "Decor"],
            imageStrategy: "Interior lifestyle photography with room scenes and product closeups",
            visualDirection: "Premium home and furniture commerce layout",
            sampleItems: []
        }
    ];

    const match = nicheMap.find((item) =>
        item.keys.some((key) => text.includes(key))
    );

    return match || "";
}

function hasMeaningfulOfferedItems(items) {
    return Array.isArray(items) &&
        items.some((item) =>
            item?.name ||
            item?.description ||
            item?.category ||
            (Array.isArray(item?.keyPoints) && item.keyPoints.length > 0)
        );
}

export function normalizeGeneratorIndustry(value = "") {
    const text = String(value || "").trim();

    if (!text) return "";

    const lower = text.toLowerCase();

    if (
        lower.includes("online store") ||
        lower.includes("ecommerce") ||
        lower.includes("e-commerce") ||
        lower.includes("commerce") ||
        lower.includes("shop") ||
        lower.includes("fashion") ||
        lower.includes("store")
    ) {
        return "Ecommerce / Store";
    }

    if (
        lower.includes("education") ||
        lower.includes("academy") ||
        lower.includes("coaching") ||
        lower.includes("school") ||
        lower.includes("course")
    ) {
        return "Education";
    }

    if (
        lower.includes("real estate") ||
        lower.includes("property") ||
        lower.includes("listing") ||
        lower.includes("portal")
    ) {
        return "Real Estate";
    }

    if (
        lower.includes("healthcare") ||
        lower.includes("clinic") ||
        lower.includes("doctor") ||
        lower.includes("medical") ||
        lower.includes("hospital")
    ) {
        return "Healthcare";
    }

    if (
        lower.includes("legal") ||
        lower.includes("law") ||
        lower.includes("lawyer")
    ) {
        return "Legal";
    }

    if (
        lower.includes("restaurant") ||
        lower.includes("cafe") ||
        lower.includes("food") ||
        lower.includes("dining")
    ) {
        return "Restaurant";
    }

    if (
        lower.includes("beauty") ||
        lower.includes("salon") ||
        lower.includes("spa")
    ) {
        return "Beauty";
    }

    if (
        lower.includes("saas") ||
        lower.includes("software") ||
        lower.includes("tech") ||
        lower.includes("app")
    ) {
        return "SaaS / Tech";
    }

    if (
        lower.includes("fitness") ||
        lower.includes("gym") ||
        lower.includes("trainer") ||
        lower.includes("workout")
    ) {
        return "Fitness";
    }

    return text;
}

export function resolveGeneratorIntentDefaults(input = {}) {
    const industry = normalizeGeneratorIndustry(input.industry || "");
    const subIndustry = String(input.subIndustry || "").trim();
    const description = String(input.description || "").toLowerCase();
    const goal = String(input.goalOfWebsite || input.callToActionType || "").toLowerCase();

    const combined = `${industry} ${subIndustry} ${description} ${goal}`.toLowerCase();

    if (
        combined.includes("fitness") ||
        combined.includes("gym") ||
        combined.includes("trainer")
    ) {
        return {
            industry: "Fitness",
            subIndustry: subIndustry || "Gym / Personal Training",
            businessModel: "service",
            primaryWebsiteMode: "program-first",
            primaryEntityType: "programs",
            locationFocus: "local",
            contentStyleHint: "fitness-performance",
            goalOfWebsite: input.goalOfWebsite || "Generate Leads",
            callToActionType: input.callToActionType || "Book Trial",
            pages: ["Home", "Programs", "Trainers", "Membership", "Contact"],
            crmModules: ["programs", "trainers", "testimonials", "faqs"]
        };
    }

    if (
        combined.includes("academy") ||
        combined.includes("education") ||
        combined.includes("coaching") ||
        combined.includes("school") ||
        combined.includes("course")
    ) {
        return {
            industry: "Education",
            subIndustry: subIndustry || "Academy / Coaching",
            businessModel: "academy",
            primaryWebsiteMode: "program-first",
            primaryEntityType: "programs",
            locationFocus: "local",
            contentStyleHint: "academy-program-first",
            goalOfWebsite: input.goalOfWebsite || "Generate Admissions",
            callToActionType: input.callToActionType || "Join Program",
            pages: ["Home", "Programs", "Coaches", "Admissions", "Contact"],
            crmModules: ["programs", "coaches", "testimonials", "faqs"]
        };
    }

    if (
        combined.includes("ecommerce") ||
        combined.includes("e-commerce") ||
        combined.includes("commerce") ||
        combined.includes("online store") ||
        combined.includes("store") ||
        combined.includes("shop") ||
        combined.includes("fashion") ||
        combined.includes("product")
    ) {
        const commerceNiche = extractCommerceNiche(input);

        return {
            industry: "Ecommerce / Store",
            subIndustry: subIndustry || (commerceNiche?.niche ? `Online Store - ${commerceNiche.niche}` : "Online Store"),
            businessModel: "ecommerce",
            primaryWebsiteMode: "product-first",
            primaryEntityType: "products",
            locationFocus: "national",
            contentStyleHint: commerceNiche?.niche
                ? `commerce-product-first ${commerceNiche.niche}`
                : "commerce-product-first",
            goalOfWebsite: input.goalOfWebsite || "Sell Products",
            callToActionType: input.callToActionType || "Buy Online",
            pages: ["Home", "Products", "Collections", "Contact"],
            crmModules: ["products", "collections", "testimonials", "faqs"],

            productNiche: commerceNiche?.niche || "",
            productCategories: commerceNiche?.categories || [],
            visualDirection: commerceNiche?.visualDirection || "",
            imageStrategy: commerceNiche?.imageStrategy || "",
            offeredItems: commerceNiche?.sampleItems || []
        };
    }

    if (
        combined.includes("real estate") ||
        combined.includes("property") ||
        combined.includes("listing") ||
        combined.includes("portal")
    ) {
        const isPortal =
            combined.includes("portal") ||
            combined.includes("marketplace") ||
            combined.includes("search");

        return {
            industry: "Real Estate",
            subIndustry: subIndustry || (isPortal ? "Property Portal" : "Real Estate Agency"),
            businessModel: isPortal ? "marketplace" : "service",
            primaryWebsiteMode: isPortal ? "search-first" : "advisory-first",
            primaryEntityType: "listings",
            locationFocus: "local",
            contentStyleHint: isPortal ? "realestate-portal" : "realestate-company",
            goalOfWebsite: input.goalOfWebsite || "Generate Leads",
            callToActionType: input.callToActionType || "Search Properties",
            pages: isPortal
                ? ["Home", "Listings", "Cities", "Contact"]
                : ["Home", "Listings", "Services", "Contact"],
            crmModules: ["listings", "localities", "testimonials", "faqs"]
        };
    }

    if (
        combined.includes("healthcare") ||
        combined.includes("clinic") ||
        combined.includes("doctor") ||
        combined.includes("medical")
    ) {
        return {
            industry: "Healthcare",
            subIndustry: subIndustry || "Clinic",
            businessModel: "service",
            primaryWebsiteMode: "appointment-first",
            primaryEntityType: "treatments",
            locationFocus: "local",
            contentStyleHint: "healthcare-clinic",
            goalOfWebsite: input.goalOfWebsite || "Book Appointments",
            callToActionType: input.callToActionType || "Book Appointment",
            pages: ["Home", "Treatments", "Doctors", "Contact"],
            crmModules: ["treatments", "doctors", "testimonials", "faqs"]
        };
    }

    if (
        combined.includes("legal") ||
        combined.includes("law") ||
        combined.includes("lawyer")
    ) {
        return {
            industry: "Legal",
            subIndustry: subIndustry || "Law Firm",
            businessModel: "service",
            primaryWebsiteMode: "consultation-first",
            primaryEntityType: "practice-areas",
            locationFocus: "local",
            contentStyleHint: "legal-trust-first",
            goalOfWebsite: input.goalOfWebsite || "Generate Consultations",
            callToActionType: input.callToActionType || "Book Consultation",
            pages: ["Home", "Practice Areas", "Team", "Contact"],
            crmModules: ["practice-areas", "lawyers", "testimonials", "faqs"]
        };
    }

    if (
        combined.includes("restaurant") ||
        combined.includes("cafe") ||
        combined.includes("food") ||
        combined.includes("dining")
    ) {
        return {
            industry: "Restaurant",
            subIndustry: subIndustry || "Restaurant / Cafe",
            businessModel: "service",
            primaryWebsiteMode: "menu-first",
            primaryEntityType: "menu-items",
            locationFocus: "local",
            contentStyleHint: "restaurant-menu-first",
            goalOfWebsite: input.goalOfWebsite || "Increase Orders",
            callToActionType: input.callToActionType || "View Menu",
            pages: ["Home", "Menu", "Specials", "Gallery", "Contact"],
            crmModules: ["menu-items", "specials", "testimonials", "faqs"]
        };
    }

    if (
        combined.includes("beauty") ||
        combined.includes("salon") ||
        combined.includes("spa")
    ) {
        return {
            industry: "Beauty",
            subIndustry: subIndustry || "Salon / Spa",
            businessModel: "service",
            primaryWebsiteMode: "booking-first",
            primaryEntityType: "services",
            locationFocus: "local",
            contentStyleHint: "beauty-services-first",
            goalOfWebsite: input.goalOfWebsite || "Book Appointments",
            callToActionType: input.callToActionType || "Book Now",
            pages: ["Home", "Services", "Specialists", "Gallery", "Contact"],
            crmModules: ["services", "specialists", "testimonials", "faqs"]
        };
    }

    if (
        combined.includes("saas") ||
        combined.includes("software") ||
        combined.includes("tech") ||
        combined.includes("app")
    ) {
        return {
            industry: "SaaS / Tech",
            subIndustry: subIndustry || "SaaS Platform",
            businessModel: "saas",
            primaryWebsiteMode: "conversion-first",
            primaryEntityType: "features",
            locationFocus: "global",
            contentStyleHint: "saas-conversion",
            goalOfWebsite: input.goalOfWebsite || "Generate Demos",
            callToActionType: input.callToActionType || "Request Demo",
            pages: ["Home", "Features", "Pricing", "Integrations", "Contact"],
            crmModules: ["features", "pricing", "testimonials", "faqs"]
        };
    }

    return {
        industry: industry || "Business",
        subIndustry: subIndustry || "",
        businessModel: "service",
        primaryWebsiteMode: "service-first",
        primaryEntityType: "services",
        locationFocus: "local",
        contentStyleHint: "service-business",
        goalOfWebsite: input.goalOfWebsite || "Generate Leads",
        callToActionType: input.callToActionType || "Contact Us",
        pages: ["Home", "Services", "Contact"],
        crmModules: ["services", "testimonials", "faqs"]
    };
}

export function enrichGeneratorPayload(rawPayload = {}) {
    const defaults = resolveGeneratorIntentDefaults(rawPayload);

    const pages =
        Array.isArray(rawPayload.pages) && rawPayload.pages.length > 0
            ? rawPayload.pages
            : defaults.pages;

    const crmModules =
        Array.isArray(rawPayload.crmModules) && rawPayload.crmModules.length > 0
            ? rawPayload.crmModules
            : defaults.crmModules;

    const offeredItems =
        hasMeaningfulOfferedItems(rawPayload.offeredItems)
            ? rawPayload.offeredItems
            : defaults.offeredItems || rawPayload.offeredItems || [];

    return {
        ...rawPayload,

        industry: rawPayload.industry || defaults.industry,
        subIndustry: rawPayload.subIndustry || defaults.subIndustry,

        businessModel: rawPayload.businessModel || defaults.businessModel,
        primaryWebsiteMode: rawPayload.primaryWebsiteMode || defaults.primaryWebsiteMode,
        primaryEntityType: rawPayload.primaryEntityType || defaults.primaryEntityType,
        locationFocus: rawPayload.locationFocus || defaults.locationFocus,
        contentStyleHint: rawPayload.contentStyleHint || defaults.contentStyleHint,

        goalOfWebsite: rawPayload.goalOfWebsite || defaults.goalOfWebsite,
        callToActionType: rawPayload.callToActionType || defaults.callToActionType,

        visualDirection: rawPayload.visualDirection || defaults.visualDirection || "",
        imageStrategy: rawPayload.imageStrategy || defaults.imageStrategy || "",

        productNiche: rawPayload.productNiche || defaults.productNiche || "",
        productCategories: rawPayload.productCategories || defaults.productCategories || [],

        pages,
        crmModules,
        offeredItems
    };
}